import * as React from "react";
import { useState, useEffect } from "react";
import {
    AudioWaveform,
    BookOpen,
    Bot,
    Command,
    Frame,
    GalleryVerticalEnd,
    Map,
    PieChart,
    Settings2,
    SquareTerminal,
    Home,
    Inbox,
    Calendar,
    Search,
    Settings,
    ShoppingCart,
    PackageOpen,
    DollarSign,
    Link,
    HelpCircle,
    Shield,
    BookText,
    Percent,
    BarChart,
    ChartSpline,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { NavUser } from "./NavUser";
import { TeamSwitcher } from "./TeamSwitcher";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarRail,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuItem,
    SidebarMenuButton,
    useSidebar,
} from "@/components/ui/sidebar";
import { ShopSwitcher } from "./ShopSwitcher";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/lib/supabaseClient";
import { useShopStore } from "@/stores/shopStore";
import { useImpersonationStore } from "@/stores/impersonationStore";

// This is sample data.
const data = {
    user: {
        name: "shadcn",
        email: "m@example.com",
        avatar: "/avatars/shadcn.jpg",
    },
    teams: [
        {
            name: "Acme Inc",
            logo: GalleryVerticalEnd,
            plan: "Enterprise",
        },
        {
            name: "Acme Corp.",
            logo: AudioWaveform,
            plan: "Startup",
        },
        {
            name: "Evil Corp.",
            logo: Command,
            plan: "Free",
        },
    ],
    items: [
        {
            title: "Overview",
            url: "/",
            icon: BarChart,
            beta: false,
        },
        // {
        //     title: "Products",
        //     url: "/products",
        //     icon: PackageOpen,
        // },
        {
            title: "Models",
            url: "/pricing-models",
            icon: ChartSpline,
        },
        // {
        //     title: "Rules",
        //     url: "/rules",
        //     icon: BookText,
        // },
        // {
        //     title: "Discounts",
        //     url: "/discounts",
        //     icon: Percent,
        //     beta: true,
        // },
        {
            title: "Integrations",
            url: "/integrations",
            icon: Link,
        },
        {
            title: "Settings",
            url: "/settings",
            icon: Settings,
        },
        {
            title: "Support",
            url: "/support",
            icon: HelpCircle,
        },
    ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
    const { state } = useSidebar();
    const { session } = useAuth();
    const isDemo = session?.user?.email === "demo@getoptifi.com";

    // Use the Zustand store
    const { activeShop, setActiveShop } = useShopStore();

    // Add this to track the current path
    const [currentPath, setCurrentPath] = useState<string>("");
    const [shops, setShops] = useState<any[]>([{ id: "1", name: "" }]);
    const [isLoadingShops, setIsLoadingShops] = useState(true);
    // Add state for the modal
    const [showModal, setShowModal] = useState(false);

    // Fetch shops from Supabase
    useEffect(() => {
        async function fetchShops() {
            setIsLoadingShops(true);
            try {
                if (isDemo) {
                    // Use demo data for demo users
                    const demoShop = { id: "1", name: "Demo Store" };
                    setShops([demoShop]);

                    // Set active shop in Zustand store
                    setActiveShop(demoShop);
                } else {
                    // Fetch real data from Supabase
                    const { data, error } = await supabase
                        .from("shops")
                        .select("id, shop")
                        .single();

                    if (data) {
                        const shopDomain = data.shop;
                        // Clean up the shop domain by removing .myshopify.com
                        const shopData = {
                            id: data.id,
                            name: shopDomain.replace(".myshopify.com", ""),
                        };
                        setShops([shopData]);

                        // Only update the active shop if it's not set yet
                        if (!activeShop) {
                            setActiveShop(shopData);
                        }
                    } else {
                        // Fallback to shop not found if no shops found
                        const demoShop = { id: "1", name: "Shop Not Found" };
                        setShops([demoShop]);

                        // Only update if not already set
                        if (!activeShop) {
                            setActiveShop(demoShop);
                        }
                    }

                    if (error) {
                        console.error("Error fetching shops:", error);
                    }
                }
            } finally {
                setIsLoadingShops(false);
            }
        }

        fetchShops();
        // Remove activeShop from the dependency array to prevent infinite loops
    }, [isDemo, setActiveShop]);

    const onShopChange = (shop: any) => {
        // Update the shop in Zustand store
        setActiveShop(shop);
    };

    const onAddNewShop = () => {
        // Show the modal instead of logging to console
        setShowModal(true);
    };

    // Update currentPath when component mounts and when URL changes
    React.useEffect(() => {
        // Set initial path
        setCurrentPath(window.location.pathname);

        const handleRouteChange = () => {
            setCurrentPath(window.location.pathname);
        };

        // Listen for popstate events (browser back/forward)
        window.addEventListener("popstate", handleRouteChange);

        // Set up an interval to check for path changes
        // This will catch programmatic redirects from child components
        const intervalId = setInterval(() => {
            if (window.location.pathname !== currentPath) {
                setCurrentPath(window.location.pathname);
            }
        }, 100);

        return () => {
            window.removeEventListener("popstate", handleRouteChange);
            clearInterval(intervalId);
        };
    }, [currentPath]);

    return (
        <>
            <Sidebar collapsible="icon" {...props}>
                <SidebarHeader>
                    {state !== "collapsed" && (
                        <div className="flex items-center gap-2 pt-2 pl-1">
                            <img
                                src="/optifi_logo.png"
                                alt="Logo"
                                className="h-8 w-auto"
                            />
                            {isDemo && (
                                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                    DEMO
                                </span>
                            )}
                        </div>
                    )}
                    {state === "collapsed" && (
                        <div className="flex flex-col items-center gap-1 pt-2">
                            <img
                                src="/optifi_icon.png"
                                alt="Logo"
                                className="h-6 w-auto"
                            />
                            {isDemo && (
                                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                                    DEMO
                                </span>
                            )}
                        </div>
                    )}
                    {/* <TeamSwitcher teams={data.teams} /> */}
                </SidebarHeader>
                <SidebarContent>
                    <SidebarGroup>
                        <SidebarGroupContent>
                            <SidebarMenu>
                                {data.items.map((item) => {
                                    // Check if this item matches the current path
                                    // Handle the special case where "/" redirects to "/dashboard"
                                    // Also handle pricing models detail pages
                                    const isActive =
                                        currentPath === item.url ||
                                        (item.url === "/" &&
                                            currentPath === "/dashboard") ||
                                        (item.url === "/pricing-models" &&
                                            currentPath.startsWith(
                                                "/pricing-models"
                                            ));

                                    return (
                                        <SidebarMenuItem key={item.title}>
                                            <SidebarMenuButton asChild>
                                                <a
                                                    href={item.url}
                                                    className={`group relative ${
                                                        isActive
                                                            ? "bg-primary/10 text-primary font-medium hover:!bg-primary/10 hover:!text-primary"
                                                            : ""
                                                    }`}
                                                >
                                                    <item.icon />
                                                    <span>{item.title}</span>
                                                    {item.beta && (
                                                        <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                                            {badgeText}
                                                        </span>
                                                    )}
                                                </a>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    );
                                })}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </SidebarContent>
                <SidebarFooter>
                    <ShopSwitcher
                        shops={shops}
                        activeShop={activeShop}
                        onShopChange={onShopChange}
                        onAddNewShop={onAddNewShop}
                        isLoading={isLoadingShops}
                    />
                </SidebarFooter>
                <SidebarRail />
            </Sidebar>

            {/* Add the modal dialog */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Feature Not Available</DialogTitle>
                        <DialogDescription>
                            Adding new shops is not yet available. This
                            functionality will be coming soon.
                        </DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </Dialog>
        </>
    );
}
